import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Grid,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { PhotoCamera } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { selectRateByShop, selectStandardRate } from '../../redux/features/todayRateSlice';
import GetCustomerProfileService from '../../services/GetCustomerProfileService';
import GetWebsiteService from '../../services/GetWebsiteService';
import OrderService from '../order/OrderService';
import MercariService from './mercariService';
import ImageUploadModal from '../../utils/ImageUploadModal';
import ShippingTypeDialog from '../../sharedComponents/ShippingTypeDialog';

// Creates the Order for a WON auction case. Laid out to mirror the beam
// "Order Details" dialog so admins see the same screen they already know —
// but here the order doesn't exist yet: submit creates it. imageUrl (first
// item photo) and shipDetail/deliveryFeeJp (from the live item data, same
// mapping as AmountCal) are derived automatically and not shown as inputs.
const AuctionOrderModal = ({ open, item, shop, orderByOptions, onClose, onCreated }) => {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [profile, setProfile] = useState(null);
  const [amount, setAmount] = useState('');
  const [qty, setQty] = useState(1);
  const [imageUrl, setImageUrl] = useState('');          // auto: first item photo
  const [itemTitle, setItemTitle] = useState(null);      // auto: original item title (for categorization)
  // Whether the live fetch supplied the value. The read-only display keys on THESE,
  // not on the value itself — keying on the value locked the manual fallback input
  // after the first typed character (value became truthy → input swapped out).
  const [amountAuto, setAmountAuto] = useState(false);
  const [imageAuto, setImageAuto] = useState(false);
  const [deliveryFeeJp, setDeliveryFeeJp] = useState(0); // auto: live item data
  const [shipDetail, setShipDetail] = useState('free');  // auto: live item data
  const [orderByOptionId, setOrderByOptionId] = useState('');
  const [isHandCarry, setIsHandCarry] = useState(false);
  // 7-Eleven fee — same as the beam pending-purchase form (checkbox + yen fee)
  const [isSeven, setIsSeven] = useState(false);
  const [sevenFee, setSevenFee] = useState('');
  const [openImageModal, setOpenImageModal] = useState(false);
  const [selectedImages, setSelectedImages] = useState([]);
  // Explicit opt-out: create the order now, someone else purchases later —
  // the confirm image gets attached afterwards via the normal confirm flow.
  const [skipConfirmImage, setSkipConfirmImage] = useState(false);
  // Ship/Air reminder — same purpose as the beam flow: nudge the admin to ask
  // the customer (or check the chat) before keying. Shown once per open.
  const [showShippingType, setShowShippingType] = useState(false);
  const todayRate = useSelector((state) => selectRateByShop(state, shop));
  const standardRate = useSelector(selectStandardRate);

  // Flow-rate customers get today's rate, not the stale profile.productRate
  // (same formula as AmountCal / OrderForm / chatconeController).
  const calculateFlowRate = () => {
    const percentageAdjustment = (todayRate / standardRate) - (profile?.extraPercentDiscount || 0);
    const newRate = standardRate * percentageAdjustment - (profile?.extraFlowDiscount || 0);
    return Math.round(newRate * 10000) / 10000;
  };

  useEffect(() => {
    if (!open || !item) return;
    let mounted = true;
    setLoading(true);
    setProfile(null);
    setSelectedImages([]);
    setSkipConfirmImage(false);
    setIsHandCarry(false);
    setIsSeven(false);
    setSevenFee('');
    setQty(1);
    // Reset per-item fields — without this, reopening on another item whose live
    // fetch fails silently kept the PREVIOUS item's price/photo/fee.
    setAmount('');
    setImageUrl('');
    setAmountAuto(false);
    setImageAuto(false);
    setDeliveryFeeJp(0);
    setShipDetail('free');

    const prefill = async () => {
      const [profileRes, itemRes] = await Promise.allSettled([
        GetCustomerProfileService.getCustomerProfileFromContact({ contact: item.customerContact, shop }),
        GetWebsiteService.getItemDataFromUrl(item.itemUrl),
      ]);
      if (!mounted) return;

      if (profileRes.status === 'fulfilled') {
        setProfile(profileRes.value.data);
      } else {
        toast.error('ไม่พบข้อมูลลูกค้า — เช็ค contact ของรายการประมูลนี้');
      }

      // Same mapping AmountCal uses: '0' → free, 'COD' → cod, numeric → normal + fee
      const data = itemRes.status === 'fulfilled' ? (itemRes.value.data?.data || itemRes.value.data || {}) : {};
      if (data.finalPrice) { setAmount(String(data.finalPrice)); setAmountAuto(true); }
      if (data.mainImage) { setImageUrl(data.mainImage); setImageAuto(true); }
      if (data.title) { setItemTitle(data.title); }
      if (data.deliveryFee === '0') {
        setDeliveryFeeJp(0);
        setShipDetail('free');
      } else if (data.deliveryFee === 'COD') {
        setDeliveryFeeJp(null);
        setShipDetail('cod');
      } else if (data.deliveryFee && !isNaN(data.deliveryFee)) {
        setDeliveryFeeJp(parseFloat(data.deliveryFee));
        setShipDetail('normal');
      }

      // Default Order By to the account that actually won
      const winner = (orderByOptions || []).find((o) => o.label === item.orderByLabel);
      setOrderByOptionId(winner ? winner.id : '');

      setLoading(false);
      setShowShippingType(true);
    };
    prefill();
    return () => { mounted = false; };
  }, [open, item]); // eslint-disable-line react-hooks/exhaustive-deps

  const deliveryFeeText = shipDetail === 'cod'
    ? 'ปลายทาง (COD)'
    : (deliveryFeeJp ? `¥${deliveryFeeJp}` : '-');

  const handleSubmit = async () => {
    if (!profile) {
      toast.error('ยังไม่มีข้อมูลลูกค้า');
      return;
    }
    if (!amount) {
      toast.warning('กรุณากรอกราคา');
      return;
    }
    if (!imageUrl) {
      toast.error('กรุณาใส่ลิงก์รูปสินค้า (ดึงอัตโนมัติไม่ได้)');
      return;
    }
    // Same rule as the beam confirm — unless the admin explicitly opted out
    // (order created first, purchase happens later by someone else)
    if (!skipConfirmImage && selectedImages.length !== 1) {
      toast.warning('กรุณาอัพโหลดรูปยืนยันการสั่งซื้อ 1 ไฟล์');
      return;
    }
    // Real money: never fall back to the stale profile rate for a flow-rate customer
    if (profile.isFlowRate && (!standardRate || !todayRate)) {
      toast.error('ยังโหลดเรทวันนี้ไม่ได้ — ลองใหม่อีกครั้ง (ลูกค้า Flow Rate)');
      return;
    }
    setSubmitting(true);
    try {
      const orderData = {
        profile: {
          id: profile.id,
          smileOrderNo: profile.smileOrderNo,
          smileName: profile.smileName,
          smileNameJp: profile.smileNameJp,
          contact: profile.contact,
          downRate: profile.downRate,
          productRate: profile.isFlowRate ? calculateFlowRate() : profile.productRate,
          codRate: profile.codRate,
          weightRate: profile.weightRate,
          transactionFee: profile.transactionFee || 0,
        },
        order: {
          url: item.itemUrl,
          imageUrl,
          itemTitle,
          orderDate: new Date().toISOString(),
          amount: parseFloat(amount),
          deliveryFeeJp: shipDetail === 'cod' ? null : deliveryFeeJp || 0,
          shipDetail,
          qty: parseInt(qty, 10) || 1,
          website: 'Mercari',
          isCredit: false,
          creditRate: profile.creditRate,
          isPre: false,
          preDate: null,
          sevenFee: isSeven && sevenFee.trim() ? parseFloat(sevenFee) : null,
          isDown: false,
          isHandCarry,
          orderByOptionId: orderByOptionId || null,
        },
      };

      // multipart like the beam confirm: orderData JSON + optional confirm image
      const formData = new FormData();
      formData.append('orderData', JSON.stringify(orderData));
      formData.append('shop', shop);
      selectedImages.forEach((image) => formData.append('images', image));

      await OrderService.addNewOrder(formData);

      // Order exists → the auction case is resolved; clear it right away instead
      // of waiting for the poller's URL match.
      try {
        await MercariService.setAuctionItemStatus({ id: item.id, status: 'done' });
        await MercariService.releaseAuctionCase(item.id);
      } catch (resolveErr) {
        console.error('auction case resolve after create failed (poller will catch it):', resolveErr);
      }

      toast.success('สร้างออเดอร์เรียบร้อย');
      onCreated();
    } catch (err) {
      console.error('AuctionOrderModal create failed:', err.response?.data || err);
      toast.error(err.response?.data?.message || err.response?.data?.error || 'สร้างออเดอร์ไม่สำเร็จ');
    } finally {
      setSubmitting(false);
    }
  };

  if (!item) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        คีย์ออเดอร์ประมูลที่ชนะ
        {item.orderByLabel && (
          <Chip label={`ชนะด้วย ${item.orderByLabel}`} color="primary" size="small" sx={{ ml: 1 }} />
        )}
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>
        ) : (
          <Box sx={{ marginTop: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">Order Date</Typography>
                <Typography variant="body1" gutterBottom>
                  {new Date().toLocaleString('th-TH', { dateStyle: 'medium', timeStyle: 'short' })}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">จบประมูล</Typography>
                <Typography variant="body1" gutterBottom>
                  {item.finishTime
                    ? new Date(item.finishTime).toLocaleString('th-TH', { dateStyle: 'medium', timeStyle: 'short' })
                    : '-'}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">Smile Name</Typography>
                <Typography variant="body1" gutterBottom>{profile?.smileName || '-'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">Smile Name (JP)</Typography>
                <Typography variant="body1" gutterBottom>{profile?.smileNameJp || '-'}</Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">Contact</Typography>
                <Typography variant="body1" gutterBottom>{item.customerContact || '-'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">Quantity</Typography>
                <Typography variant="body1" gutterBottom>{qty}</Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">Amount (ราคาชนะประมูล)</Typography>
                {amountAuto ? (
                  <Typography variant="body1" gutterBottom>¥{amount}</Typography>
                ) : (
                  // Fallback only: live price fetch failed (item removed / API down)
                  <TextField
                    label="กรอกราคาชนะ (¥) — ดึงอัตโนมัติไม่ได้"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    type="number"
                    size="small"
                    sx={{ width: 260 }}
                  />
                )}
              </Grid>

              {!imageAuto && (
                <Grid item xs={12}>
                  {/* Fallback only: sold/removed items may return no photo */}
                  <TextField
                    label="Image URL (ดึงรูปอัตโนมัติไม่ได้ — วางลิงก์รูปสินค้า)"
                    value={imageUrl}
                    onChange={(e) => setImageUrl(e.target.value)}
                    fullWidth
                    size="small"
                  />
                </Grid>
              )}
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">ค่าส่งในญี่ปุ่น</Typography>
                <Typography variant="body1" gutterBottom>{deliveryFeeText}</Typography>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" color="textSecondary">Product URL</Typography>
                <Typography variant="body1" gutterBottom>
                  <a
                    href={item.itemUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ wordBreak: 'break-all', color: '#1976d2' }}
                  >
                    {item.itemUrl}
                  </a>
                </Typography>
              </Grid>
            </Grid>

            <Box sx={{ marginTop: 3, padding: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
              <Typography variant="h6" gutterBottom>Shipping Options</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
                <FormControl fullWidth size="small">
                  <InputLabel id="auction-order-by-label" shrink>Order By</InputLabel>
                  <Select
                    labelId="auction-order-by-label"
                    value={orderByOptionId || ''}
                    label="Order By"
                    notched
                    displayEmpty
                    onChange={(e) => setOrderByOptionId(e.target.value ? parseInt(e.target.value, 10) : null)}
                  >
                    <MenuItem value="" disabled><em>กรุณาเลือก</em></MenuItem>
                    {(orderByOptions || []).map((opt) => (
                      <MenuItem key={opt.id} value={opt.id}>{opt.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isHandCarry}
                      onChange={(e) => setIsHandCarry(e.target.checked)}
                      color="primary"
                    />
                  }
                  label="รายการหิ้ว (Hand Carry)"
                />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={isSeven}
                        onChange={(e) => {
                          setIsSeven(e.target.checked);
                          if (!e.target.checked) setSevenFee('');
                        }}
                        color="primary"
                      />
                    }
                    label="จ่าย Seven (Seven-Eleven)"
                  />
                  {isSeven && (
                    <TextField
                      label="Seven Fee (เยน)"
                      value={sevenFee}
                      onChange={(e) => setSevenFee(e.target.value)}
                      size="small"
                      type="number"
                      sx={{ width: 160 }}
                      placeholder="0"
                    />
                  )}
                </Box>
              </Box>

              <Typography variant="h6" gutterBottom>Upload Image</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Button
                    variant="outlined"
                    startIcon={<PhotoCamera />}
                    onClick={() => setOpenImageModal(true)}
                  >
                    Upload รูปยืนยันการสั่งซื้อ
                  </Button>
                  {selectedImages.length > 0 && (
                    <Chip label="1 file selected" color="primary" variant="outlined" />
                  )}
                </Box>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={skipConfirmImage}
                      onChange={(e) => setSkipConfirmImage(e.target.checked)}
                      color="primary"
                    />
                  }
                  label="ยังไม่แนบรูปยืนยัน — สร้างออเดอร์ก่อน รอผู้อื่นกดซื้อ"
                />
              </Box>
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || submitting || !profile}
        >
          {submitting ? <CircularProgress size={20} color="inherit" /> : 'Submit'}
        </Button>
      </DialogActions>

      <ImageUploadModal
        isOpen={openImageModal}
        onRequestClose={() => setOpenImageModal(false)}
        onConfirm={(images) => { setSelectedImages(images); setOpenImageModal(false); }}
        title="Upload Order Image"
        maxFiles={1}
      />

      <ShippingTypeDialog
        open={showShippingType}
        onClose={() => { setShowShippingType(false); onClose(); }}
        onSelect={(type) => { setIsHandCarry(type === 'Air'); setShowShippingType(false); }}
        hasEverHadAirOrder={!!profile?.hasEverHadAirOrder}
      />
    </Dialog>
  );
};

export default AuctionOrderModal;
